// Failover to the backup API host when the main API pool is unreachable.
//
// The backup is a single small isi_data + api_balancer host in its own DNS and
// routing pool (host group "backup API", frontend backup.sp2api2.isilive.ca),
// deliberately outside the main api/hls pools so it stays reachable when the
// main pool's hosts, balancers, or DNS are broken.
//
// Behavior: any cookie-agnostic request to the primary API base that dies with
// a network error — or a 502/503/504 straight from a dying balancer — is
// retried once against the backup base, and the console then STICKS to the
// backup for every subsequent API request. While failed over, the primary is
// probed every 30s with GET /user; as soon as it answers anything below 500
// the console switches back. The backup hostname is a subdomain of the primary
// (backup.sp2api2.isilive.ca) so the isi_token cookie, whose Domain is the
// primary host, is sent to it too — auth works unchanged on the backup.

import axios from "axios";
import { ISI_API_URL, ISI_API_BACKUP_URL } from "../config";

const PROBE_INTERVAL_MS = 30000;

let failedOver = false;
let probeTimer = null;

// Network-level death or a gateway error — the "primary pool is unreachable"
// signals. Application errors (4xx, plain 500s from a live backend) are NOT
// failover triggers: the pool answered, the request itself was bad.
function isGatewayFailure(error) {
  if (error.code === "ERR_CANCELED") return false;
  if (!error.response) return true; // DNS failure, refused, timeout, TLS
  const status = error.response.status;
  return status === 502 || status === 503 || status === 504;
}

// While failed over, poll the primary until it answers again. GET /user is the
// cheapest authenticated endpoint and re-minting the isi_token on the way back
// is a bonus. Any response below 500 counts as recovered — a 502/503/504 means
// the balancer is up but its backends still aren't.
function startProbingPrimary() {
  if (probeTimer) return;
  probeTimer = setInterval(async () => {
    try {
      const res = await axios.get(ISI_API_URL + "user", {
        withCredentials: true,
        _skipFailover: true,
        _skipAuthRetry: true,
        timeout: 10000,
        validateStatus: () => true,
      });
      if (res.status < 500) {
        failedOver = false;
        clearInterval(probeTimer);
        probeTimer = null;
        console.warn(
          "API failover: primary API is back — switching back to",
          ISI_API_URL,
        );
      }
    } catch (probeErr) {
      // Still down — keep probing.
    }
  }, PROBE_INTERVAL_MS);
}

export function installApiFailover() {
  if (!ISI_API_BACKUP_URL) return;

  // While failed over, transparently point every primary-API request at the
  // backup base. Call sites keep building URLs from ISI_API_URL.
  axios.interceptors.request.use((cfg) => {
    const url = cfg.url || "";
    if (failedOver && !cfg._skipFailover && url.startsWith(ISI_API_URL)) {
      cfg.url = ISI_API_BACKUP_URL + url.slice(ISI_API_URL.length);
    }
    return cfg;
  });

  // First failure on the primary: flip to failover mode and retry the request
  // once against the backup. Requests already on the backup base don't match
  // the primary prefix, so a dead backup can't loop.
  axios.interceptors.response.use(undefined, async (error) => {
    const cfg = error.config;
    if (!cfg || cfg._skipFailover || cfg._failoverRetried) {
      return Promise.reject(error);
    }
    const url = cfg.url || "";
    if (!url.startsWith(ISI_API_URL) || !isGatewayFailure(error)) {
      return Promise.reject(error);
    }
    if (!failedOver) {
      failedOver = true;
      console.warn(
        "API failover: primary API unreachable — switching to",
        ISI_API_BACKUP_URL,
      );
    }
    startProbingPrimary();
    cfg._failoverRetried = true;
    cfg.url = ISI_API_BACKUP_URL + url.slice(ISI_API_URL.length);
    return axios(cfg);
  });
}
